import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Check,
  ChevronRight,
  MoreHorizontal,
  Download,
  FileDown,
  ClipboardCopy,
  Code,
  Database,
  EyeOff,
  ListPlus,
  FileText,
  History,
  MoveHorizontal,
  LayoutTemplate,
  SmilePlus,
  Users,
  Waypoints,
} from 'lucide-react';
import { setAiHidden, useAiHidden } from '../ai/privacy';
import { AiWrite } from '../ai/AiWrite';
import { ExtractTasksDialog } from '../ai/ExtractTasks';
import type { Editor } from '@tiptap/core';
import { NexusEditor } from '@nexus/editor/react';
import { parseMarkdown } from '@nexus/markdown';
import type { PageRecord } from '@nexus/store';
import { archiveDoneTasks, makeDatabase, unmakeDatabase } from '@nexus/workspace';
import { isOpen } from '@nexus/core';
import { DropdownMenu } from 'radix-ui';
import { useTasks } from '../db/tasks';
import { exportPage } from '../lib/pageExport';
import { useStore, useStoreQuery } from '../db/context';
import { ConfirmDialog } from './Dialog';
import { useDatabase } from '../db/database';
import { usePageDoc } from '../db/content';
import { useEditorHost } from '../editor/host';
import { applyMarkdown, pageToMarkdown, resolveLinks } from '../editor/markdown';
import { useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { BacklinksPanel } from './BacklinksPanel';
import { DatabaseView } from './database/DatabaseView';
import { PropertiesPanel } from './PropertiesPanel';
import { ProjectPanel } from './tasks/ProjectPanel';
import { VersionsDialog } from './VersionsDialog';
import { Breadcrumb } from './Breadcrumb';
import { IconPicker } from './IconPicker';
import { useTemplatesFolderId } from '../templates/folder';
import { openUseTemplate, placeCursorMarker, useTemplateUi } from '../templates/state';
import { PageIcon } from './PageIcon';
import { useMemberNames, useOwnUserId, useWorkspaceUi } from '../workspaces/state';

const SAVE_DELAY_MS = 300;

// CodeMirror is only needed in source mode; keep it out of the main bundle.
const SourceEditor = lazy(() =>
  import('./SourceEditor').then((m) => ({ default: m.SourceEditor })),
);

/** Plain-text paste that looks like Markdown is converted instead of pasted literally. */
const MARKDOWN_HINT = /(^|\n)(#{1,6} |[-*+] |\d+\. |> |```|\|)|\[\[|\*\*|__|`[^`]+`|\[[^\]]+\]\(/;

function handlePaste(editor: Editor, event: ClipboardEvent): boolean {
  const data = event.clipboardData;
  if (!data || data.types.includes('text/html')) return false;
  const text = data.getData('text/plain');
  if (!text || !MARKDOWN_HINT.test(text)) return false;
  if (editor.state.selection.$from.parent.type.spec.code) return false;
  const doc = resolveLinks(parseMarkdown(text).doc);
  return editor.commands.insertContent(doc.content ?? []);
}

function Title({ page, readOnly }: { page: PageRecord; readOnly?: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const [title, setTitle] = useState(page.title);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Follow external changes (sidebar rename) unless the user is typing here.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setTitle(page.title);
  }, [page.title]);

  // New, untitled pages start with the cursor in the title.
  useEffect(() => {
    if (!page.title) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id]);

  const flush = () => {
    clearTimeout(timer.current);
    if (pending.current !== null) {
      void store.updatePage(page.id, { title: pending.current });
      pending.current = null;
    }
  };
  // Never drop a pending edit when the view goes away (tab switch, close).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => flush, []);

  // Long titles wrap: grow the textarea to its content (also when the width changes).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title]);

  const titleClass =
    'block w-full resize-none overflow-hidden bg-transparent text-3xl leading-tight font-bold tracking-tight outline-none sm:text-4xl placeholder:text-fg-muted/60';

  if (readOnly) {
    return <h1 className={titleClass}>{title || t('page.titlePlaceholder')}</h1>;
  }

  return (
    <textarea
      ref={inputRef}
      rows={1}
      value={title}
      placeholder={t('page.titlePlaceholder')}
      aria-label={t('page.rename')}
      onChange={(e) => {
        // A title is one line: pasted line breaks become spaces.
        const value = e.target.value.replace(/\s*[\r\n]+\s*/g, ' ');
        setTitle(value);
        pending.current = value;
        clearTimeout(timer.current);
        timer.current = setTimeout(flush, SAVE_DELAY_MS);
      }}
      onBlur={flush}
      onKeyDown={(e) => {
        // Enter moves into the document, like in Notion.
        if (e.key === 'Enter') {
          e.preventDefault();
          flush();
          (document.querySelector('.nx-editor') as HTMLElement | null)?.focus();
        }
      }}
      className={titleClass}
    />
  );
}

/** The check mark of an on/off item in the page menu. */
function Tick({ on }: { on: boolean }) {
  return <Check className={on ? 'size-4 text-accent-ink' : 'size-4 opacity-0'} />;
}

export function PageView({
  page,
  children,
  readOnly,
}: {
  page: PageRecord;
  children: PageRecord[];
  /** A read-only workspace membership: the whole page becomes non-editable. */
  readOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const doc = usePageDoc(page.id);
  const host = useEditorHost();
  const [archived, setArchived] = useState<number | null>(null);
  const finishedTasks =
    useTasks()?.filter((x) => x.kind === 'inline' && x.pageId === page.id && !isOpen(x.status))
      .length ?? 0;
  const editorRef = useRef<Editor | null>(null);
  const aliases = useCatalog((s) => s.aliases.get(page.id));
  const [source, setSource] = useState<{ text: string; props: Record<string, unknown> } | null>(
    null,
  );
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [copied, setCopied] = useState(false);
  const fullWidth = useUi((s) => s.pageWidth[page.id] ?? s.fullWidthDefault);
  const focusTask = useUi((s) => s.focusTask);
  const db = useDatabase(page.id);
  const [confirmDb, setConfirmDb] = useState<'make' | 'unmake' | null>(null);
  // Taken, Projecten and Vergaderingen are used by Nexus itself: they stay databases.
  const builtIn = useStoreQuery(
    async (s) => {
      for (const key of ['tasks_parent_id', 'projects_db_id', 'meetings_db_id'])
        if ((await s.getSetting(key)) === page.id) return true;
      return false;
    },
    [page.id],
    ['settings'],
  );
  const aiHidden = useAiHidden(page.id);
  const isTemplate = useTemplatesFolderId() === page.parentId && !!page.parentId;
  const cursorPage = useTemplateUi((s) => s.cursorPage);

  // Just made from a template: the cursor goes where the template had {{cursor}}.
  useEffect(() => {
    if (!editor || !doc || cursorPage !== page.id) return;
    let tries = 0;
    const timer = setInterval(() => {
      if (placeCursorMarker(editor) || ++tries > 20) {
        clearInterval(timer);
        useTemplateUi.getState().set({ cursorPage: null });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [editor, doc, cursorPage, page.id]);

  // Coming from a notification: scroll to the task and highlight it briefly.
  useEffect(() => {
    if (!doc || focusTask?.pageId !== page.id) return;
    const timer = setTimeout(() => {
      const el = document.querySelector<HTMLElement>(
        `.nx-editor li[data-task-id="${focusTask.taskId}"]`,
      );
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el?.classList.add('nx-flash');
      setTimeout(() => el?.classList.remove('nx-flash'), 2000);
      useUi.getState().set({ focusTask: null });
    }, 300);
    return () => clearTimeout(timer);
  }, [doc, focusTask, page.id]);
  const setPageWidth = useUi((s) => s.setPageWidth);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const ownUserId = useOwnUserId();
  const memberNames = useMemberNames(page.workspaceId);
  const lastEditorName =
    page.workspaceId && page.lastEditedBy && page.lastEditedBy !== ownUserId
      ? (memberNames[page.lastEditedBy] ?? null)
      : null;

  const currentMarkdown = async () => {
    const props = await store.getProps(page.id);
    const json = editorRef.current?.getJSON() ?? { type: 'doc', content: [] };
    return { text: pageToMarkdown(json, props), props };
  };

  const toggleSource = async () => {
    if (source) {
      setSource(null);
      setSourceError(null);
    } else {
      setSource(await currentMarkdown());
    }
  };

  const applySource = async () => {
    if (!source) return;
    const { doc: next, invalidFrontmatter } = await applyMarkdown(
      store,
      page.id,
      source.text,
      source.props,
    );
    if (invalidFrontmatter !== null) {
      setSourceError(t('source.invalidFrontmatter'));
      return;
    }
    editorRef.current?.commands.setContent(next);
    setSource(null);
    setSourceError(null);
  };

  const menuContent =
    'nx-pop-in z-[70] min-w-56 rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
  const menuItem =
    'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 outline-none data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[highlighted]:bg-bg-hover';
  const toolbarButton =
    'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs whitespace-nowrap text-fg-muted hover:bg-bg-hover hover:text-fg';

  return (
    <article
      className={`mx-auto w-full px-6 py-6 sm:px-10 ${fullWidth ? 'max-w-none' : 'max-w-6xl'}`}
    >
      {/* The breadcrumb, with the page's actions in one menu on the right (and
          "archive finished tasks" next to it when there are any). */}
      <div className="mb-4 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Breadcrumb page={page} />
        </div>
        <div
          role="toolbar"
          aria-label={t('page.toolbar')}
          className="flex shrink-0 items-center gap-1"
        >
          {readOnly && (
            <span className="rounded-full bg-bg-subtle px-2.5 py-1 text-xs text-fg-muted">
              {t('page.readOnly')}
            </span>
          )}
          {!readOnly && finishedTasks > 0 && (
            <button
              type="button"
              className={toolbarButton}
              title={t('archive.hint')}
              onClick={async () => {
                const n = await archiveDoneTasks(store, page.id);
                setArchived(n);
                setTimeout(() => setArchived(null), 2500);
              }}
            >
              <Archive className="size-3.5" />
              {archived !== null
                ? t('archive.done', { count: archived })
                : t('archive.button', { count: finishedTasks })}
            </button>
          )}
          {copied && <span className="text-xs text-fg-muted">{t('source.copied')}</span>}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={t('page.menu')}
                title={t('page.menu')}
                className="grid size-8 place-items-center rounded-full text-fg-muted hover:bg-bg-hover hover:text-fg data-[state=open]:bg-bg-hover"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end" sideOffset={4} className={menuContent}>
                {!readOnly && (
                  <DropdownMenu.CheckboxItem
                    className={menuItem}
                    checked={!!source}
                    onSelect={() => void toggleSource()}
                  >
                    <Code className="size-4" />
                    <span className="flex-1">{t('source.toggle')}</span>
                    <Tick on={!!source} />
                  </DropdownMenu.CheckboxItem>
                )}
                <DropdownMenu.CheckboxItem
                  className={menuItem}
                  checked={fullWidth}
                  onSelect={() => setPageWidth(page.id, !fullWidth)}
                >
                  <MoveHorizontal className="size-4" />
                  <span className="flex-1">{t('page.fullWidth')}</span>
                  <Tick on={fullWidth} />
                </DropdownMenu.CheckboxItem>
                {!readOnly && aiHidden && (
                  <DropdownMenu.CheckboxItem
                    className={menuItem}
                    checked={aiHidden.own || aiHidden.inherited}
                    disabled={aiHidden.inherited}
                    title={aiHidden.inherited ? t('ai.hiddenInherited') : t('ai.hideHint')}
                    onSelect={() => void setAiHidden(store, page.id, !aiHidden.own)}
                  >
                    <EyeOff className="size-4" />
                    <span className="flex-1">{t('ai.hidden')}</span>
                    <Tick on={aiHidden.own || aiHidden.inherited} />
                  </DropdownMenu.CheckboxItem>
                )}
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                {!readOnly && aiHidden && !aiHidden.own && !aiHidden.inherited && (
                  <DropdownMenu.Item className={menuItem} onSelect={() => setExtractOpen(true)}>
                    <ListPlus className="size-4" />
                    {t('ai.extract.button')}
                  </DropdownMenu.Item>
                )}
                {!readOnly && db === null && (
                  <DropdownMenu.Item className={menuItem} onSelect={() => setConfirmDb('make')}>
                    <Database className="size-4" />
                    {t('db.convert')}
                  </DropdownMenu.Item>
                )}
                {!readOnly && db && builtIn === false && (
                  <DropdownMenu.Item className={menuItem} onSelect={() => setConfirmDb('unmake')}>
                    <Database className="size-4" />
                    {t('db.unconvert')}
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  className={menuItem}
                  onSelect={() => open({ kind: 'graph', center: page.id })}
                >
                  <Waypoints className="size-4" />
                  {t('graph.local')}
                </DropdownMenu.Item>
                <DropdownMenu.Item className={menuItem} onSelect={() => setVersionsOpen(true)}>
                  <History className="size-4" />
                  {t('versions.title')}
                </DropdownMenu.Item>
                {page.workspaceId && (
                  <DropdownMenu.Item
                    className={menuItem}
                    onSelect={() => useWorkspaceUi.getState().openManage(page.workspaceId!)}
                  >
                    <Users className="size-4" />
                    {t('workspaces.members')}
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <DropdownMenu.Sub>
                  <DropdownMenu.SubTrigger className={menuItem}>
                    <Download className="size-4" />
                    <span className="flex-1">{t('export.button')}</span>
                    <ChevronRight className="size-4 text-fg-muted" />
                  </DropdownMenu.SubTrigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.SubContent sideOffset={6} className={menuContent}>
                      <DropdownMenu.Item
                        className={menuItem}
                        onSelect={async () => {
                          await navigator.clipboard.writeText((await currentMarkdown()).text);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1500);
                        }}
                      >
                        <ClipboardCopy className="size-4" />
                        {t('source.copy')}
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator className="my-1 h-px bg-border" />
                      {(['md', 'html', 'pdf', 'docx'] as const).map((format) => (
                        <DropdownMenu.Item
                          key={format}
                          className={menuItem}
                          onSelect={async () =>
                            void exportPage(store, format, {
                              title: page.title || t('page.untitled'),
                              doc: editorRef.current?.getJSON() ?? { type: 'doc', content: [] },
                              markdown: (await currentMarkdown()).text,
                            })
                          }
                        >
                          <FileDown className="size-4" />
                          {t(`export.${format}`)}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Portal>
                </DropdownMenu.Sub>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      {readOnly ? (
        page.icon && (
          <p className="mb-1 px-1 text-5xl leading-tight">
            <PageIcon icon={page.icon} />
          </p>
        )
      ) : (
        <IconPicker pageId={page.id}>
          {page.icon ? (
            <button
              type="button"
              aria-label={t('page.icon.change')}
              title={t('page.icon.change')}
              className="mb-1 rounded-md px-1 text-5xl leading-tight hover:bg-bg-hover"
            >
              <PageIcon icon={page.icon} />
            </button>
          ) : (
            <button
              type="button"
              className="mb-1 flex items-center gap-1 rounded px-1 py-0.5 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              <SmilePlus className="size-4" />
              {t('page.icon.add')}
            </button>
          )}
        </IconPicker>
      )}
      {isTemplate && (
        <div
          role="note"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm"
        >
          <LayoutTemplate className="size-4 text-fg-muted" />
          <span className="flex-1">{t('templates.banner')}</span>
          <button
            type="button"
            onClick={() => useTemplateUi.getState().set({ settingsFor: page.id })}
            className="rounded-md px-2 py-1 hover:bg-bg-hover"
          >
            {t('templates.settings')}
          </button>
          <button
            type="button"
            onClick={() => openUseTemplate({ templateId: page.id, mode: 'page' })}
            className="rounded-full bg-accent px-2 py-1 font-medium text-accent-fg"
          >
            {t('templates.use')}
          </button>
        </div>
      )}
      <Title page={page} readOnly={readOnly} />
      <p className="mt-2 text-xs text-fg-muted">
        {t('page.created')} {fmt.format(page.createdAt)} · {t('page.updated')}{' '}
        {fmt.format(page.updatedAt)}
        {lastEditorName && ` · ${t('page.lastEditedBy', { name: lastEditorName })}`}
      </p>

      {!source && db === null && <PropertiesPanel pageId={page.id} readOnly={readOnly} />}
      {!source && <ProjectPanel pageId={page.id} readOnly={readOnly} />}
      {!source && db && <DatabaseView pageId={page.id} db={db} readOnly={readOnly} />}

      <div className="mt-6">
        {source && (
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm">
              <span className="flex-1 text-fg-muted">{t('source.hint')}</span>
              <button
                type="button"
                className="rounded-md px-2 py-1 hover:bg-bg-hover"
                onClick={() => void toggleSource()}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="rounded-full bg-accent px-3 py-1 font-medium text-accent-fg"
                onClick={() => void applySource()}
              >
                {t('source.apply')}
              </button>
            </div>
            {sourceError && (
              <p role="alert" className="mb-2 text-sm text-danger">
                {sourceError}
              </p>
            )}
            <Suspense fallback={null}>
              <SourceEditor
                initial={source.text}
                onChange={(text) => setSource((s) => (s ? { ...s, text } : s))}
              />
            </Suspense>
          </div>
        )}
        <div hidden={!!source}>
          {doc && (
            <NexusEditor
              key={page.id}
              document={doc}
              host={host}
              handlePaste={handlePaste}
              readOnly={readOnly}
              onReady={(ed) => {
                editorRef.current = ed;
                setEditor(ed);
              }}
            />
          )}
        </div>
      </div>

      {children.length > 0 && !db && (
        <section className="mt-8">
          <h2 className="mb-2 text-sm font-medium text-fg-muted">{t('page.subpages')}</h2>
          <ul className="flex flex-col">
            {children.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => open({ kind: 'page', pageId: c.id })}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-bg-hover"
                >
                  <FileText className="size-4 text-fg-muted" />
                  <span className="underline decoration-border underline-offset-4">
                    {c.title || t('page.untitled')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <BacklinksPanel pageId={page.id} names={[page.title, ...(aliases ?? [])].filter(Boolean)} />

      {!source && !readOnly && (
        <>
          <AiWrite
            editor={editor}
            title={page.title}
            hidden={!!aiHidden && (aiHidden.own || aiHidden.inherited)}
          />
        </>
      )}
      <ExtractTasksDialog
        open={extractOpen}
        onOpenChange={setExtractOpen}
        pageId={page.id}
        title={page.title}
        editor={editor}
      />
      <ConfirmDialog
        open={confirmDb !== null}
        onOpenChange={(o) => !o && setConfirmDb(null)}
        title={confirmDb === 'unmake' ? t('db.unconvertTitle') : t('db.convertTitle')}
        description={
          confirmDb === 'unmake'
            ? t('db.unconvertConfirm', { title: page.title || t('page.untitled') })
            : t('db.convertConfirm', { title: page.title || t('page.untitled') })
        }
        confirmLabel={confirmDb === 'unmake' ? t('db.unconvert') : t('db.convert')}
        cancelLabel={t('db.cancel')}
        onConfirm={() => {
          if (confirmDb === 'make') void makeDatabase(store, page.id);
          if (confirmDb === 'unmake') void unmakeDatabase(store, page.id);
          setConfirmDb(null);
        }}
      />
      <VersionsDialog
        pageId={page.id}
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        onRestore={(json) => editorRef.current?.commands.setContent(json)}
      />
    </article>
  );
}
